import React, { useState, useEffect, useCallback, useRef } from "react";

import { toast } from "react-toastify";

import PhoneIcon from "@material-ui/icons/PhoneInTalkOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import api from "../../services/api";
import { connectToSocket } from "../../services/socket";
import { useConfirm } from "../../components/ConfirmDialog";
import { formatPhone } from "../../utils/formatPhone";

// Aba "Conversas" do agente: lista as conversas que a IA conduziu — WhatsApp
// (navegador / Transferir para a IA) e LIGAÇÕES de voz — com ícone do canal. Ao
// clicar, abre a thread: bolhas, chips de botões/lista e o card de resultado; na
// voz, a transcrição + o player da gravação (quando o agente grava ligações).
// WhatsApp: GET /agents/:id/conversations/:convKey · voz ("call:<id>"):
// GET /agents/:id/calls/:id (+ /recording).
const T = {
  line: "#E6E9ED",
  ink: "#1B222A",
  ink3: "#929AA4",
  bot: "#EEF0F3",
  nav: "#E7F6EC",
  human: "#E3EEFB", // balão do operador (atendente humano) — azul, distinto da IA
  humanInk: "#1F5FA6",
  ok: "#1E7E34",
  chip: "#2f6fb0",
};

const isCall = k => String(k || "").startsWith("call:");
// desfecho da ligação (AgentCalls.outcome)
const OUTCOME = { concluida: "IA encerrou", transferida: "transferida", encerrada_contato: "cliente desligou", caixa_postal: "caixa postal", ocupado: "ocupado", nao_atendeu: "não atendeu", falhou: "falhou" };
// tentativa sem conversa (não houve transcrição/áudio): ícone apagado na lista
const NO_TALK = { ocupado: 1, nao_atendeu: 1, falhou: 1 };
const dur = sec => {
  const n = Math.max(0, Number(sec) || 0);
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};
// ícone do canal na lista e no cabeçalho
const ChannelIcon = ({ voice, size = 18, muted = false }) => (
  <span
    title={muted ? "Ligação sem conversa" : voice ? "Ligação (voz)" : "WhatsApp"}
    style={{
      width: size + 12, height: size + 12, flexShrink: 0, borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: muted ? "#EEF0F3" : voice ? "#EAF1F8" : "rgba(37,211,102,0.12)", color: muted ? "#929AA4" : voice ? "#2F6FB0" : "#1E9E62",
    }}
  >
    {voice ? <PhoneIcon style={{ fontSize: size }} /> : <WhatsAppIcon style={{ fontSize: size }} />}
  </span>
);

const fmt = t => {
  try {
    return new Date(t).toLocaleString("pt-BR");
  } catch {
    return "";
  }
};

const ResultCard = ({ r }) => (
  <div style={{ alignSelf: "center", maxWidth: "85%", background: "#fff", border: `1px solid ${T.line}`, borderLeft: `3px solid ${T.ok}`, borderRadius: 10, padding: "12px 14px", margin: "8px 0" }}>
    <div style={{ margin: "0 0 6px", fontSize: 12, color: T.ok, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700 }}>Resultado</div>
    <table style={{ borderCollapse: "collapse" }}>
      <tbody>
        {Object.keys(r || {}).map(k => (
          <tr key={k}>
            <td style={{ padding: "2px 8px 2px 0", color: T.ink3, whiteSpace: "nowrap", verticalAlign: "top" }}>{k}</td>
            <td style={{ padding: "2px 0", verticalAlign: "top" }}>{typeof r[k] === "object" ? JSON.stringify(r[k]) : String(r[k])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// chamada de FUNÇÃO da IA (ferramenta, transferir…): item centralizado e discreto;
// clicar mostra os parâmetros e o retorno.
const pretty = v => {
  if (v == null || v === "") return "—";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};
const ToolItem = ({ t }) => {
  const [open, setOpen] = useState(false);
  const ok = t.ok !== false;
  return (
    <div style={{ alignSelf: "center", maxWidth: "85%", margin: "2px 0" }}>
      <div
        onClick={() => setOpen(o => !o)}
        title="Ver parâmetros e retorno"
        style={{
          display: "inline-flex", alignItems: "center", gap: 7, cursor: "pointer", userSelect: "none",
          background: "#fff", border: `1px solid ${ok ? T.line : "#E3B7B7"}`, borderRadius: 999, padding: "4px 12px",
          fontSize: 12, color: ok ? "#5B6470" : "#B12A2A",
        }}
      >
        <span style={{ fontSize: 13 }}>⚙</span>
        <span>
          IA chamou <b style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 600 }}>{t.name}</b>
          {ok ? "" : " — falhou"}
        </span>
        <span style={{ color: T.ink3 }}>{open ? "▴" : "▾"}</span>
      </div>
      {open && (
        <div style={{ marginTop: 6, background: "#fff", border: `1px solid ${T.line}`, borderRadius: 10, padding: "10px 12px", fontSize: 12 }}>
          <div style={{ color: T.ink3, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", fontSize: 10.5, marginBottom: 3 }}>Parâmetros</div>
          <pre style={{ margin: "0 0 8px", whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 11.5 }}>{pretty(t.args)}</pre>
          <div style={{ color: T.ink3, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", fontSize: 10.5, marginBottom: 3 }}>Retorno</div>
          <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 11.5, maxHeight: 220, overflow: "auto" }}>{pretty(t.result)}</pre>
        </div>
      )}
    </div>
  );
};

// ── Anexos (estilo WhatsApp) ─────────────────────────────────────────────
// O backend grava em m.attachments ({ status, name, mime, kind, sizeBytes, assetId,
// caption, limitMb }); conversas antigas trazem só a nota interna da IA no texto
// ("[ARQUIVO RECEBIDO E SALVO: …storage?file=N]") — convertemos para o mesmo cartão.
const FILE_BADGE = {
  pdf: { label: "PDF", color: "#E2483D" },
  doc: { label: "DOC", color: "#2F6FD0" },
  xls: { label: "XLS", color: "#1D8A4C" },
  image: { label: "IMG", color: "#8E5BD6" },
};
const fmtKb = b => {
  const n = Number(b) || 0;
  if (!n) return "";
  return n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1).replace(".", ",")} MB` : `${Math.max(1, Math.round(n / 1024))} kB`;
};
const extOfName = n => ((/\.([a-z0-9]{1,5})$/i.exec(String(n || "")) || [])[1] || "").toLowerCase();
const kindOfName = (n, mime) => {
  const e = extOfName(n);
  if (String(mime || "").startsWith("image/") || ["jpg", "jpeg", "png", "webp", "gif", "heic"].includes(e)) return "image";
  if (e === "pdf" || mime === "application/pdf") return "pdf";
  if (["doc", "docx"].includes(e)) return "doc";
  if (["xls", "xlsx"].includes(e)) return "xls";
  return null;
};
// retrocompat: extrai o anexo da nota interna gravada no texto
const NOTE_RE = /\[ARQUIVO (RECEBIDO E SALVO|MUITO GRANDE|NÃO ACEITO|RECEBIDO): ([^\]]*?)(?: \(| tem | —)[^\]]*\](?: Legenda: "([^"]*)"\.)?/g;
const splitNotes = text => {
  const atts = [];
  const rest = String(text || "").replace(NOTE_RE, (all, st, name, caption) => {
    const id = (/storage\?file=(\d+)/.exec(all) || [])[1];
    const status = st === "RECEBIDO E SALVO" ? "saved" : st === "MUITO GRANDE" ? "too_big" : st === "NÃO ACEITO" ? "rejected" : "failed";
    const size = (/ (\d+[,.]?\d*) MB/.exec(all) || [])[1];
    atts.push({ status, name: name.trim(), kind: kindOfName(name), assetId: id ? Number(id) : undefined, caption: caption || undefined, sizeBytes: size ? Number(size.replace(",", ".")) * 1024 * 1024 : undefined });
    return "";
  });
  return { atts, rest: rest.trim() };
};

// conteúdo do arquivo privado (só autenticado) → object URL
const useFileBlob = (assetId, enabled) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!enabled || !assetId) return undefined;
    let alive = true;
    let obj = null;
    api
      .get(`/storage/files/${assetId}/download`, { responseType: "blob" })
      .then(({ data }) => {
        obj = URL.createObjectURL(data);
        if (alive) setUrl(obj);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [assetId, enabled]);
  return url;
};

const openAsset = async assetId => {
  const tab = window.open("", "_blank"); // antes do await (senão o navegador bloqueia)
  try {
    const { data } = await api.get(`/storage/files/${assetId}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    if (tab) tab.location.href = url;
    else window.location.href = url;
  } catch (e) {
    if (tab) tab.close();
    toast.error("Não consegui abrir o arquivo.");
  }
};

const Attachment = ({ a }) => {
  const kind = a.kind || kindOfName(a.name, a.mime);
  const saved = a.status === "saved" && a.assetId;
  const thumb = useFileBlob(a.assetId, saved && kind === "image");
  const problem =
    a.status === "too_big"
      ? `Muito grande${a.sizeBytes ? ` · ${fmtKb(a.sizeBytes)}` : ""}${a.limitMb ? ` (limite ${a.limitMb} MB)` : ""}`
      : a.status === "rejected"
      ? "Formato não aceito"
      : a.status === "failed"
      ? "Não foi salvo"
      : "";
  if (saved && kind === "image") {
    return (
      <div onClick={() => openAsset(a.assetId)} title="Abrir imagem" style={{ cursor: "pointer", borderRadius: 8, overflow: "hidden", background: "#dfe3e7", minWidth: 200, minHeight: thumb ? 0 : 140, maxWidth: 260 }}>
        {thumb ? <img src={thumb} alt={a.name} style={{ display: "block", width: "100%", maxHeight: 300, objectFit: "cover" }} /> : null}
      </div>
    );
  }
  const badge = FILE_BADGE[kind] || { label: (extOfName(a.name) || "ARQ").toUpperCase().slice(0, 4), color: "#8A939D" };
  const meta = [badge.label, a.status === "saved" ? fmtKb(a.sizeBytes) : ""].filter(Boolean).join(" • ");
  return (
    <div
      onClick={saved ? () => openAsset(a.assetId) : undefined}
      title={saved ? "Abrir arquivo" : problem}
      style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(0,0,0,.05)", borderRadius: 8, padding: "9px 11px", minWidth: 220, maxWidth: 320, cursor: saved ? "pointer" : "default", opacity: problem ? 0.75 : 1 }}
    >
      {/* ícone de "folha" com a sigla do tipo, como o WhatsApp */}
      <div style={{ position: "relative", width: 30, height: 38, flexShrink: 0, background: "#fff", borderRadius: 3, boxShadow: "0 0 0 1px rgba(0,0,0,.08)" }}>
        <span style={{ position: "absolute", left: -3, right: -3, bottom: 5, background: badge.color, color: "#fff", fontSize: 8.5, fontWeight: 800, textAlign: "center", borderRadius: 2, padding: "1px 0", letterSpacing: ".02em" }}>{badge.label}</span>
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13.5, color: T.ink, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</div>
        <div style={{ fontSize: 11.5, color: problem ? "#B4532A" : T.ink3, marginTop: 1 }}>{problem || meta}</div>
      </div>
    </div>
  );
};

const Bubble = ({ m }) => {
  if (m.result) return <ResultCard r={m.result} />;
  if (m.tool) return <ToolItem t={m.tool} />;
  const human = m.from === "human"; // operador (atendente humano)
  const mine = human || m.from === "nav"; // nosso lado (direita): IA ou operador
  const who = human ? "Atendente" : m.from === "nav" ? "IA" : "Contato";
  const hasOpts = m.options && m.options.length;
  const bg = human ? T.human : mine ? T.nav : T.bot;
  // anexos: os gravados + os extraídos da nota interna (conversas antigas)
  const legacy = !m.attachments && !mine ? splitNotes(m.text) : { atts: [], rest: m.text };
  const atts = m.attachments || legacy.atts;
  const captions = atts.map(a => a.caption).filter(Boolean);
  const bodyText = [legacy.rest, ...captions.filter(c => !String(legacy.rest || "").includes(c))].filter(Boolean).join("\n");
  return (
    <div>
      <div style={{ fontSize: 10.5, color: human ? T.humanInk : T.ink3, fontWeight: human ? 700 : 400, textTransform: "uppercase", letterSpacing: ".04em", margin: "0 4px 2px", textAlign: mine ? "right" : "left" }}>
        {who}
        {hasOpts ? <span style={{ fontSize: 10, background: "#EAF1F8", color: "#2F6FB0", borderRadius: 6, padding: "1px 6px", marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>{m.kind === "list" ? "📋 lista" : "🔘 botões"}</span> : null}
      </div>
      <div style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start" }}>
        <div style={{ maxWidth: "70%", padding: atts.length ? "4px 4px 6px" : "9px 13px", borderRadius: 14, whiteSpace: "pre-wrap", wordBreak: "break-word", background: bg, ...(mine ? { borderTopRightRadius: 4 } : { borderTopLeftRadius: 4 }) }}>
          {atts.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {atts.map((a, i) => <Attachment key={i} a={a} />)}
            </div>
          ) : null}
          {atts.length ? (bodyText ? <div style={{ padding: "6px 9px 0" }}>{bodyText}</div> : null) : m.text || ""}
          {hasOpts ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
              {m.options.map((o, i) => (
                <span key={i} style={{ background: "#fff", border: "1px solid #cfd6dd", borderRadius: 14, padding: "3px 11px", fontSize: 12.5, color: T.chip }}>{o}</span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const AgentConversations = ({ agentId, initialConvKey }) => {
  const { confirm } = useConfirm();
  const [list, setList] = useState([]);
  const [cur, setCur] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ending, setEnding] = useState(false);
  const [takeover, setTakeover] = useState(false); // operador assumiu esta conversa?
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [working, setWorking] = useState(false); // assumir/devolver em andamento
  const autoOpened = useRef(false);
  const scrollRef = useRef(null); // painel de mensagens — rola pro fim ao carregar
  const prevRef = useRef({ cur: null, n: 0 }); // p/ só rolar quando chega msg nova
  const curRef = useRef(null); // espelha `cur` p/ o callback do socket não pegar valor velho
  const [call, setCall] = useState(null); // detalhe da ligação de voz aberta
  const [audioUrl, setAudioUrl] = useState(""); // gravação (blob) da ligação aberta
  const [audioState, setAudioState] = useState(""); // "" | loading | error

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get(`/agents/${agentId}/conversations`);
      setList(data.conversations || []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // busca o transcript + estado (atendimento humano ativo? tem linha?). silent=true
  // é usado pelo polling ao vivo — não limpa as mensagens num erro de rede passageiro.
  const fetchConv = useCallback(
    async (convKey, silent) => {
      if (isCall(convKey)) return; // ligação não muda depois de registrada
      try {
        const { data } = await api.get(`/agents/${agentId}/conversations/${encodeURIComponent(convKey)}`);
        setMessages(data.messages || []);
        setTakeover(!!data.takeover);
      } catch {
        if (!silent) setMessages([]);
      }
    },
    [agentId]
  );

  // gravação: o endpoint exige o token, então baixa como blob e toca local
  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  const openCall = async convKey => {
    const id = String(convKey).slice(5);
    setCall(null);
    setMessages([]);
    setTakeover(false);
    setAudioUrl("");
    setAudioState("");
    try {
      const { data } = await api.get(`/agents/${agentId}/calls/${encodeURIComponent(id)}`);
      if (curRef.current !== convKey) return;
      setCall(data);
      const msgs = (data.transcript || []).map(t =>
        t.role === "tool"
          ? { from: "tool", tool: { name: t.name, args: t.args, ok: t.ok, result: t.result } }
          : { from: t.role === "assistant" ? "nav" : "bot", text: t.text }
      );
      if (data.result && Object.keys(data.result).length) msgs.push({ result: data.result });
      setMessages(msgs);
      if (data.hasRecording) {
        setAudioState("loading");
        try {
          const r = await api.get(`/agents/${agentId}/calls/${encodeURIComponent(id)}/recording`, { responseType: "blob" });
          if (curRef.current !== convKey) return;
          setAudioUrl(URL.createObjectURL(r.data));
          setAudioState("");
        } catch {
          setAudioState("error");
        }
      }
    } catch {
      setMessages([]);
    }
  };

  const open = convKey => {
    curRef.current = convKey;
    setCur(convKey);
    setDraft("");
    if (isCall(convKey)) openCall(convKey);
    else {
      setCall(null);
      setAudioUrl("");
      fetchConv(convKey);
    }
  };
  const curVoice = isCall(cur);

  // AO VIVO por EVENTO (socket): o backend emite "conv.update" na sala do dono a
  // cada gravação no transcript (IA, operador ou inbound no atendimento humano).
  // A tela recarrega a conversa aberta na hora — sem polling. No (re)connect,
  // reconcilia buscando de novo. Entra/sai com a aba.
  useEffect(() => {
    const socket = connectToSocket();
    socket.emit("joinRoom", "conversations");
    const refresh = () => {
      if (curRef.current) fetchConv(curRef.current, true);
      loadList();
    };
    const onUpdate = p => {
      const k = p && p.convKey ? String(p.convKey) : "";
      if (k && curRef.current && k === String(curRef.current)) fetchConv(curRef.current, true);
      loadList();
    };
    socket.on("conv.update", onUpdate);
    socket.on("connect", refresh);
    return () => {
      socket.off("conv.update", onUpdate);
      socket.off("connect", refresh);
      socket.emit("leaveRoom", "conversations");
      socket.disconnect();
    };
  }, [fetchConv, loadList]);

  // ASSUMIR: a IA para e o operador passa a responder.
  const assumir = async () => {
    if (!cur || working) return;
    const ok = await confirm({
      title: "Assumir o atendimento",
      message: "Você vai assumir esta conversa. A IA para de responder e as mensagens passam a ser suas até você devolver.",
      confirmText: "Assumir",
    });
    if (!ok) return;
    setWorking(true);
    try {
      await api.post(`/agents/${agentId}/conversations/${encodeURIComponent(cur)}/takeover`);
      setTakeover(true);
      toast.success("Você assumiu o atendimento — a IA está pausada.");
      fetchConv(cur, true);
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.error) || "Não foi possível assumir.");
    } finally {
      setWorking(false);
    }
  };

  // DEVOLVER: encerra o atendimento humano; a próxima mensagem volta ao fluxo.
  const devolver = async () => {
    if (!cur || working) return;
    const ok = await confirm({
      title: "Devolver o atendimento",
      message: "Encerrar o atendimento humano? A próxima mensagem do contato volta ao fluxo normal (a IA reassume a frio).",
      confirmText: "Devolver",
    });
    if (!ok) return;
    setWorking(true);
    try {
      await api.post(`/agents/${agentId}/conversations/${encodeURIComponent(cur)}/release`);
      setTakeover(false);
      toast.success("Atendimento devolvido ao fluxo.");
      fetchConv(cur, true);
    } catch {
      toast.error("Não foi possível devolver.");
    } finally {
      setWorking(false);
    }
  };

  // ENVIAR como atendente (exige ter assumido).
  const sendMsg = async () => {
    const text = draft.trim();
    if (!text || !cur || sending) return;
    setSending(true);
    try {
      await api.post(`/agents/${agentId}/conversations/${encodeURIComponent(cur)}/send`, { text });
      setDraft("");
      fetchConv(cur, true);
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.error) || "Não foi possível enviar.");
    } finally {
      setSending(false);
    }
  };

  // ao carregar/trocar a conversa, rola a barra DO PAINEL de mensagens pro FIM
  // (última mensagem). Adiado 2 frames para o layout dos balões já ter assentado
  // (senão scrollHeight é medido antes do reflow e a rolagem para no meio). Mexe
  // só no container das mensagens (scrollRef), nunca na página.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const changedConv = prevRef.current.cur !== cur;
    const grew = messages.length > prevRef.current.n;
    prevRef.current = { cur, n: messages.length };
    // um refresh que não trouxe nada novo não deve puxar o operador pro fim se ele
    // subiu para ler o histórico — só rola ao abrir a conversa ou ao chegar msg.
    if (!changedConv && !grew) return undefined;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [messages, cur]);

  // auto-abre a conversa pedida por link (ex.: botão "Ver conversa" da SAÍDA do
  // nó "Iniciar conversa com agente de IA"). Uma vez só.
  useEffect(() => {
    if (initialConvKey && !autoOpened.current) {
      autoOpened.current = true;
      open(String(initialConvKey));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConvKey]);

  const endConversation = async () => {
    if (!cur || ending) return;
    const ok = await confirm({
      title: "Encerrar sessão da IA",
      message: "Encerrar a sessão da IA nesta conversa? A IA para de responder e o número volta ao fluxo normal.",
      confirmText: "Encerrar",
      danger: true,
    });
    if (!ok) return;
    setEnding(true);
    try {
      await api.post(`/agents/${agentId}/conversations/${encodeURIComponent(cur)}/end`);
      toast.success("Conversa encerrada — a IA soltou o canal.");
    } catch {
      toast.error("Não foi possível encerrar.");
    } finally {
      setEnding(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.ink3 }}>Carregando conversas…</div>;

  if (!list.length) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: T.ink3 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.ink, marginBottom: 6 }}>Nenhuma conversa ainda</div>
        <div style={{ fontSize: 13 }}>Quando este agente atender uma ligação ou conduzir uma conversa no WhatsApp, ela aparece aqui.</div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: 16, height: "62vh", minHeight: 360 }}>
      <div style={{ width: 280, flexShrink: 0, border: `1px solid ${T.line}`, borderRadius: 12, overflow: "auto", background: "#fff" }}>
        {list.map(c => (
          <div
            key={c.convKey}
            onClick={() => open(c.convKey)}
            style={{ padding: "12px 14px", borderBottom: `1px solid ${T.line}`, cursor: "pointer", background: c.convKey === cur ? "#F0F7F1" : "transparent", boxShadow: c.convKey === cur ? `inset 3px 0 0 ${T.ok}` : "none" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <ChannelIcon voice={c.channel === "voice"} muted={c.channel === "voice" && !!NO_TALK[c.outcome]} size={16} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, color: T.ink }}>
                  {c.channel === "voice" ? (c.from ? formatPhone(c.from) : "Ligação") : c.convKey}
                </div>
                <div style={{ color: T.ink3, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {c.channel === "voice"
                    ? `${c.direction === "outbound" ? "Ligação feita" : c.direction === "inbound" ? "Ligação recebida" : "Ligação"} · ${dur(c.durationSec)}${OUTCOME[c.outcome] ? ` · ${OUTCOME[c.outcome]}` : ""} · ${fmt(c.updatedAt)}`
                    : (c.last || "") + " · " + fmt(c.updatedAt)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ flex: 1, border: `1px solid ${T.line}`, borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column", background: "#F6F7F9" }}>
        {cur && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderBottom: `1px solid ${T.line}`, background: "#fff" }}>
            <ChannelIcon voice={curVoice} muted={curVoice && call && !!NO_TALK[call.outcome]} size={16} />
            <div style={{ fontWeight: 600, color: T.ink, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {curVoice ? (call && call.contact ? formatPhone(call.contact) : "Ligação") : cur}
              {curVoice && call ? (
                <span style={{ fontWeight: 400, color: T.ink3, fontSize: 12.5, marginLeft: 8 }}>
                  {call.direction === "outbound" ? "ligação feita" : call.direction === "inbound" ? "ligação recebida" : "ligação"} · {dur(call.durationSec)}{OUTCOME[call.outcome] ? ` · ${OUTCOME[call.outcome]}` : ""} · {fmt(call.startedAt)}
                </span>
              ) : null}
            </div>
            {curVoice ? null : takeover ? (
              <button
                type="button"
                onClick={devolver}
                disabled={working}
                title="Encerra o atendimento humano; a próxima mensagem do contato volta ao fluxo (a IA reassume)."
                style={{ border: "1px solid #CBD9EA", background: "#fff", color: T.humanInk, borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, cursor: working ? "default" : "pointer", opacity: working ? 0.6 : 1 }}
              >
                {working ? "…" : "Devolver ao fluxo"}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={assumir}
                  disabled={working}
                  title="Assume a conversa: a IA para e você passa a responder."
                  style={{ border: "none", background: T.humanInk, color: "#fff", borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, cursor: working ? "default" : "pointer", opacity: working ? 0.55 : 1 }}
                >
                  {working ? "…" : "Assumir atendimento"}
                </button>
                <button
                  type="button"
                  onClick={endConversation}
                  disabled={ending}
                  title="Solta a sessão da IA: ela para de responder e o número volta ao fluxo normal."
                  style={{ border: "1px solid #E3B7B7", background: "#fff", color: "#B12A2A", borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, cursor: ending ? "default" : "pointer", opacity: ending ? 0.6 : 1 }}
                >
                  {ending ? "Encerrando…" : "Encerrar conversa"}
                </button>
              </>
            )}
          </div>
        )}
        {curVoice && call && !NO_TALK[call.outcome] && (
          <div style={{ padding: "10px 14px", borderBottom: `1px solid ${T.line}`, background: "#fff" }}>
            {call.hasRecording ? (
              audioUrl ? (
                <audio controls src={audioUrl} style={{ width: "100%", height: 36 }} />
              ) : (
                <div style={{ color: T.ink3, fontSize: 12.5 }}>{audioState === "error" ? "Não foi possível carregar a gravação." : "Carregando a gravação…"}</div>
              )
            ) : (
              <div style={{ color: T.ink3, fontSize: 12.5 }}>
                Ligação sem gravação de áudio. Para gravar as próximas, ligue “Gravar ligações” em Canais → Telefone (voz).
              </div>
            )}
          </div>
        )}
        {cur && !curVoice && takeover && (
          <div style={{ padding: "6px 14px", background: "#EAF2FB", color: T.humanInk, fontSize: 12, fontWeight: 600, borderBottom: `1px solid ${T.line}` }}>
            Você assumiu este atendimento — a IA está pausada.
          </div>
        )}
        <div ref={scrollRef} style={{ flex: 1, overflow: "auto", padding: 18, display: "flex", flexDirection: "column", gap: 10 }}>
          {cur ? (
            messages.length ? (
              messages.map((m, i) => <Bubble key={i} m={m} />)
            ) : (
              <div style={{ color: T.ink3, textAlign: "center", margin: "auto" }}>{curVoice ? (call ? "Ligação sem transcrição." : "Carregando…") : "Conversa vazia."}</div>
            )
          ) : (
            <div style={{ color: T.ink3, textAlign: "center", margin: "auto" }}>Escolha uma conversa à esquerda.</div>
          )}
        </div>
        {cur && !curVoice && (
          <div style={{ borderTop: `1px solid ${T.line}`, background: "#fff", padding: 10 }}>
            {takeover ? (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <textarea
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMsg();
                    }
                  }}
                  placeholder="Escreva como atendente…  (Enter envia · Shift+Enter quebra linha)"
                  rows={1}
                  style={{ flex: 1, resize: "none", border: `1px solid ${T.line}`, borderRadius: 10, padding: "9px 12px", fontSize: 13.5, fontFamily: "inherit", maxHeight: 120, outline: "none" }}
                />
                <button
                  type="button"
                  onClick={sendMsg}
                  disabled={sending || !draft.trim()}
                  style={{ border: "none", background: sending || !draft.trim() ? "#9FB8D6" : T.humanInk, color: "#fff", borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: sending || !draft.trim() ? "default" : "pointer" }}
                >
                  {sending ? "Enviando…" : "Enviar"}
                </button>
              </div>
            ) : (
              <div style={{ textAlign: "center", color: T.ink3, fontSize: 12.5, padding: "4px 0" }}>
                Assuma o atendimento para responder como atendente.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentConversations;
